import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { otterTheme } from "../../theme";
import { Mic, CheckCircleOutline, WorkspacePremium, Business, AccessTime } from "@mui/icons-material";
import { useSignUp } from "@clerk/clerk-react";
import Header from "../common/Header";
import Footer from "../common/Footer";
import axios from "axios";
import { showSuccessToast, showErrorToast } from "../../toastUtils";

// Constante para el límite de transcripciones (igual al de AudioTranscriber)
const TRANSCRIPTION_LIMIT = 25;

// URL para enviar la solicitud de contacto de Enterprise
const API_URL = process.env.REACT_APP_API_URL;

interface EnterpriseFormData {
  name: string;
  email: string;
  company: string;
  employees: string;
  message: string;
}

const StartForFree: React.FC = () => {
  const navigate = useNavigate();
  const { isLoaded, signUp } = useSignUp();
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [formData, setFormData] = useState<EnterpriseFormData>({
    name: "",
    email: "",
    company: "",
    employees: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState<Partial<EnterpriseFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  
  // Verificar la configuración al cargar el componente
  useEffect(() => {
    if (!API_URL) {
      setConfigError("API URL not configured. Please set REACT_APP_API_URL in your environment variables.");
      console.error("Missing REACT_APP_API_URL environment variable");
    }
  }, []);

  // Función para manejar el inicio del proceso de registro con Clerk
  const handleGetStarted = () => {
    // Redireccionar a la página de registro en lugar de login
    navigate("/signup");
  };

  const handleUpgradeToPro = () => {
    // Enviar a una nueva ruta dedicada exclusivamente para este flujo
    navigate("/direct-pro-signup");
  };

  const handleOpenEnterpriseModal = () => {
    // Si hay un error de configuración, mostrar una notificación de error en lugar de abrir el modal
    if (!API_URL) {
      showErrorToast("API URL not configured. Enterprise inquiry form is disabled.");
      return;
    }
    setShowEnterpriseModal(true);
  };

  const handleCloseEnterpriseModal = () => {
    setShowEnterpriseModal(false);
    setFormData({
      name: "",
      email: "",
      company: "",
      employees: "",
      message: "",
    });
    setFormErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Limpiar el error cuando el usuario escribe
    if (formErrors[name as keyof EnterpriseFormData]) {
      setFormErrors({
        ...formErrors,
        [name]: undefined,
      });
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Limpiar el error cuando el usuario selecciona
    if (formErrors[name as keyof EnterpriseFormData]) {
      setFormErrors({
        ...formErrors,
        [name]: undefined,
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<EnterpriseFormData> = {};
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = "Name is required";
      isValid = false;
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
      isValid = false;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      errors.email = "Invalid email address";
      isValid = false;
    }

    if (!formData.company.trim()) {
      errors.company = "Company name is required";
      isValid = false;
    }

    if (!formData.employees) {
      errors.employees = "Please select an option";
      isValid = false;
    }

    if (!formData.message.trim()) {
      errors.message = "Message is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmitEnterpriseForm = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Verificar si la API URL está configurada
    if (!API_URL) {
      showErrorToast("API URL not configured. Form submission is disabled.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('Sending enterprise inquiry to:', `${API_URL}/contact`);
      
      await axios.post(`${API_URL}/contact`, {
        ...formData,
        type: 'enterprise',
        subject: 'Enterprise Plan Inquiry',
      });
      
      setIsSubmitting(false);
      handleCloseEnterpriseModal();
      showSuccessToast("Thank you! We've received your enterprise inquiry and will get back to you within 24 hours.");
    } catch (error) {
      console.error('Error sending message:', error);
      setIsSubmitting(false);
      showErrorToast("Failed to send message. Please try again later.");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minHeight: "100vh", 
        backgroundColor: "#f4f6f8",
      }}
    >
      <Header />

      <Box 
        sx={{ 
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingTop: 6,
          paddingBottom: 6,
          px: 2
        }}
      >
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: "bold", 
            mb: 2, 
            textAlign: "center",
            color: "#333"
          }}
        >
          Start transcribing your audio today
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 4, 
            maxWidth: "800px", 
            textAlign: "center",
            color: "#666"
          }}
        >
          Tranzcribe converts spoken words into text using AI. Our product organizes and processes 
          your dictation to generate tasks, helping to save time, increase efficiency, 
          and reduce mental effort during the day.
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 4,
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            maxWidth: "1200px",
          }}
        >
          {/* Free Plan Card */}
          <Card
            sx={{
              width: { xs: "100%", sm: "350px" },
              padding: 3,
              backgroundColor: "#ffffff",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              borderRadius: "16px",
              transition: "transform 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              },
            }}
          >
            <CardContent sx={{ textAlign: "center", p: 2 }}>
              <IconButton
                disableRipple
                sx={{
                  backgroundColor: "#e0f2fe",
                  color: "#0284c7",
                  mb: 2,
                  fontSize: "1.5rem",
                }}
              >
                <Mic />
              </IconButton>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "#0284c7", mb: 1 }}
              >
                Personal
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ marginBottom: 2 }}
              >
                Perfect for trying out Tranzcribe
              </Typography>

              <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                $0
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Limited to {TRANSCRIPTION_LIMIT} transcriptions
              </Typography>

              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#0284c7",
                  color: "#fff",
                  marginTop: 2,
                  marginBottom: 2,
                  borderRadius: "8px",
                  padding: "10px 16px",
                  textTransform: "none",
                  fontWeight: "600",
                  width: "100%",
                  "&:hover": {
                    backgroundColor: "#0369a1",
                  },
                }}
                onClick={handleGetStarted}
                disabled={!isLoaded}
              >
                Get Started for Free
              </Button>

              <Divider sx={{ marginY: 2 }} />

              <Box sx={{ textAlign: "left", mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    mb: 1.5,
                    color: "#666" 
                  }}
                >
                  <CheckCircleOutline sx={{ mr: 1, fontSize: "1rem", color: "#0284c7" }} /> 
                  Up to {TRANSCRIPTION_LIMIT} audio transcriptions
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    mb: 1.5,
                    color: "#666" 
                  }}
                >
                  <CheckCircleOutline sx={{ mr: 1, fontSize: "1rem", color: "#0284c7" }} /> 
                  AI summaries of transcriptions
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    mb: 1.5,
                    color: "#666" 
                  }}
                >
                  <CheckCircleOutline sx={{ mr: 1, fontSize: "1rem", color: "#0284c7" }} /> 
                  Email and document templates
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ 
                    display: "flex", 
                    alignItems: "center",
                    color: "#666" 
                  }}
                >
                  <CheckCircleOutline sx={{ mr: 1, fontSize: "1rem", color: "#0284c7" }} /> 
                  Basic text editing tools
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Pro Plan Card */}
          <Card
            sx={{
              width: { xs: "100%", sm: "350px" },
              padding: 3,
              backgroundColor: "#ffffff",
              boxShadow: "0 4px 20px rgba(93,95,239,0.15)",
              borderRadius: "16px",
              border: "2px solid #5D5FEF",
              transition: "transform 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 8px 30px rgba(93,95,239,0.2)",
              },
            }}
          >
            <CardContent sx={{ textAlign: "center", p: 2 }}>
              <IconButton
                disableRipple
                sx={{
                  backgroundColor: "#ede9fe",
                  color: "#5D5FEF",
                  mb: 2,
                  fontSize: "1.5rem",
                }}
              >
                <WorkspacePremium />
              </IconButton>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "#5D5FEF", mb: 1 }}
              >
                Pro
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ marginBottom: 2 }}
              >
                For power users who need more
              </Typography>

              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                $9.99
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                per month, unlimited transcriptions
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 2, 
                  color: "#5D5FEF", 
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <AccessTime sx={{ mr: 0.5, fontSize: "0.9rem" }} />
                Includes 14-day free trial
              </Typography>

              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#5D5FEF",
                  color: "#fff",
                  marginTop: 2,
                  marginBottom: 2,
                  borderRadius: "8px",
                  padding: "10px 16px",
                  textTransform: "none",
                  fontWeight: "600",
                  width: "100%",
                  "&:hover": {
                    backgroundColor: "#4A4BD5",
                  },
                }}
                onClick={handleUpgradeToPro}
              >
                Get Pro
              </Button>

              <Divider sx={{ marginY: 2 }} />

              <Box sx={{ textAlign: "left", mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    mb: 1.5,
                    color: "#666" 
                  }}
                >
                  <CheckCircleOutline sx={{ mr: 1, fontSize: "1rem", color: "#5D5FEF" }} /> 
                  <strong>Unlimited</strong>&nbsp; audio transcriptions
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    mb: 1.5,
                    color: "#666" 
                  }}
                >
                  <CheckCircleOutline sx={{ mr: 1, fontSize: "1rem", color: "#5D5FEF" }} /> 
                  Advanced AI summaries and analysis
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    mb: 1.5,
                    color: "#666" 
                  }}
                >
                  <CheckCircleOutline sx={{ mr: 1, fontSize: "1rem", color: "#5D5FEF" }} /> 
                  Priority support
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ 
                    display: "flex", 
                    alignItems: "center",
                    color: "#666" 
                  }}
                >
                  <CheckCircleOutline sx={{ mr: 1, fontSize: "1rem", color: "#5D5FEF" }} /> 
                  All features from Personal plan
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Enterprise Plan Card */}
          <Card
            sx={{
              width: { xs: "100%", sm: "350px" },
              padding: 3,
              backgroundColor: "#ffffff",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              borderRadius: "16px",
              transition: "transform 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              },
            }}
          >
            <CardContent sx={{ textAlign: "center", p: 2 }}>
              <IconButton
                disableRipple
                sx={{
                  backgroundColor: "#f0fdf4",
                  color: "#16a34a",
                  mb: 2,
                  fontSize: "1.5rem",
                }}
              >
                <Business />
              </IconButton>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "#16a34a", mb: 1 }}
              >
                Enterprise
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ marginBottom: 2 }}
              >
                For organizations with high volume needs
              </Typography>

              <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                Custom
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Tailored to your organization's needs
              </Typography>

              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#16a34a",
                  color: "#fff",
                  marginTop: 2,
                  marginBottom: 2,
                  borderRadius: "8px",
                  padding: "10px 16px",
                  textTransform: "none",
                  fontWeight: "600",
                  width: "100%",
                  "&:hover": {
                    backgroundColor: "#15803d",
                  },
                }}
                onClick={handleOpenEnterpriseModal}
              >
                Contact Sales
              </Button>

              <Divider sx={{ marginY: 2 }} />

              <Box sx={{ textAlign: "left", mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    mb: 1.5,
                    color: "#666" 
                  }}
                >
                  <CheckCircleOutline sx={{ mr: 1, fontSize: "1rem", color: "#16a34a" }} /> 
                  Unlimited transcription
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    mb: 1.5,
                    color: "#666" 
                  }}
                >
                  <CheckCircleOutline sx={{ mr: 1, fontSize: "1rem", color: "#16a34a" }} /> 
                  Highest accuracy
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    mb: 1.5,
                    color: "#666" 
                  }}
                >
                  <CheckCircleOutline sx={{ mr: 1, fontSize: "1rem", color: "#16a34a" }} /> 
                  Dedicated support
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ 
                    display: "flex", 
                    alignItems: "center",
                    color: "#666" 
                  }}
                >
                  <CheckCircleOutline sx={{ mr: 1, fontSize: "1rem", color: "#16a34a" }} /> 
                  Custom integrations
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Enterprise Modal */}
      <Dialog 
        open={showEnterpriseModal} 
        onClose={handleCloseEnterpriseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>
          Enterprise Plan Inquiry
        </DialogTitle>
        
        <DialogContent>
          <Box component="form" onSubmit={handleSubmitEnterpriseForm} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={!!formErrors.name}
              helperText={formErrors.name}
              required
            />
            
            <TextField
              fullWidth
              margin="normal"
              label="Business Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              required
            />
            
            <TextField
              fullWidth
              margin="normal"
              label="Company Name"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              error={!!formErrors.company}
              helperText={formErrors.company}
              required
            />
            
            <FormControl 
              fullWidth 
              margin="normal" 
              error={!!formErrors.employees}
              required
            >
              <InputLabel>Number of Employees</InputLabel>
              <Select
                value={formData.employees}
                name="employees"
                label="Number of Employees"
                onChange={handleSelectChange}
              >
                <MenuItem value="">Select...</MenuItem>
                <MenuItem value="1-10">1-10</MenuItem>
                <MenuItem value="11-50">11-50</MenuItem>
                <MenuItem value="51-200">51-200</MenuItem>
                <MenuItem value="201-500">201-500</MenuItem>
                <MenuItem value="501+">501+</MenuItem>
              </Select>
              {formErrors.employees && (
                <FormHelperText>{formErrors.employees}</FormHelperText>
              )}
            </FormControl>
            
            <TextField
              fullWidth
              margin="normal"
              label="Message"
              name="message"
              multiline
              rows={4}
              value={formData.message}
              onChange={handleInputChange}
              error={!!formErrors.message}
              helperText={formErrors.message}
              placeholder="Tell us about your transcription needs..."
              required
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseEnterpriseModal}
            sx={{ color: "gray" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitEnterpriseForm}
            disabled={isSubmitting}
            sx={{ 
              bgcolor: "#16a34a",
              "&:hover": {
                bgcolor: "#15803d"
              }
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                Sending...
              </>
            ) : (
              "Send Inquiry"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </Box>
  );
};

export default StartForFree;
